import { ReactElement, useState } from 'react'

import { useAtomValue } from 'jotai'
import {
  IconArrowRotateRightLeft,
  Button,
  Tooltip,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { useNavigate } from 'react-router-dom'

import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import {
  isSavePendingAtom,
  invoiceIdAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { usePermissions } from 'lib/rbac'
import { RecalculationFlipResultDialog } from 'CreditNotes/components/dialog/RecalculationFlipResultDialog'

export const ButtonRecalculateInvoice = () => {
  const isSavePending = useAtomValue(isSavePendingAtom)
  const label = 'Recalculate invoice'

  const { can } = usePermissions()

  if (!can('org.invoice:update')) {
    return null
  }

  return (
    <DialogRecalculateInvoice
      trigger={
        <Tooltip label={label}>
          <Button
            variant="secondary"
            icon={<IconArrowRotateRightLeft color="inherit" />}
            aria-label={label}
            disabled={isSavePending}
            data-testid="header.button.recalculate-invoice"
          />
        </Tooltip>
      }
    />
  )
}

const DialogRecalculateInvoice = ({ trigger }: { trigger: ReactElement }) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()

  const invoiceId = useAtomValue(invoiceIdAtom)

  const [createdCreditNoteId, setCreatedCreditNoteId] = useState<string | null>(
    null
  )

  const recalculateInvoiceMutation = useMutation(
    dashboard20240730Client.postRecalculateInvoice,
    {
      onError: () => {
        displayNotification('Failed to recalculate invoice', { type: 'error' })
      }
    }
  )

  return (
    <>
      <SimpleModal
        data-testid="modal.recalculate-invoice"
        trigger={trigger}
        variant="dialog"
        title="Recalculate invoice"
        submitButtonText="Recalculate invoice"
        submittingButtonText="Recalculating..."
        onSubmit={async () => {
          if (!invoiceId) return false
          const result = await recalculateInvoiceMutation.mutateAsync({
            id: invoiceId
          })

          if (!result) return false

          if ('createdCreditNoteId' in result) {
            setCreatedCreditNoteId(result.createdCreditNoteId)
            return true
          }

          displayNotification('Invoice recalculated', { type: 'success' })
          return true
        }}
      >
        Recalculating the invoice will update line items based on the latest
        usage data.
      </SimpleModal>
      {createdCreditNoteId && (
        <RecalculationFlipResultDialog
          isOpen
          onClose={() => setCreatedCreditNoteId(null)}
          resultType="credit-note"
          onConfirm={() => {
            navigate(`/credit-notes/${createdCreditNoteId}`, { replace: true })
          }}
        />
      )}
    </>
  )
}
